import React from "react";
import { toast } from "sonner";
import { usd } from "../../lib/api";
import { Users, Download, CheckCircle2, DollarSign, Clock, Heart } from "lucide-react";
import { Stat, SponsorStatusBadge, fmtUtc, H1 } from "./AdminShared";

const ASSIGNABLE = ["awaiting_assignment", "approved", "pending_review"];

function sponsorDisplayName(s) {
  if (s.anonymous) return "Anonymous";
  if (s.acknowledgment_name) return s.acknowledgment_name;
  if (s.sponsor_type === "corporate") return s.sponsor.company;
  return `${s.sponsor.first_name || ""} ${s.sponsor.last_name || ""}`;
}

function allocationLabel(a) {
  return a.mode === "nominate" ? `Nominated: ${a.nominee?.first_name || ""} ${a.nominee?.last_name || ""}` : "Organization selects";
}

function AllocationRow({ a, reference, onAction }) {
  const assign = () => {
    const em = window.prompt("Guardian email for the invitation:");
    const cn = window.prompt("Child's name (optional):");
    if (em) onAction(reference, `allocations/${a.alloc_id}/assign`, { guardian_email: em, child_name: cn }).then((d) => {
      if (!d) return;
      toast.success("Invitation sent");
      if (d.invite_link) window.prompt("Invitation link (copy it in case the email doesn't arrive):", d.invite_link);
    });
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <span className="text-slate-600">Alloc {a.alloc_id} &middot; {allocationLabel(a)} &middot; <b>{a.status.replace(/_/g, " ")}</b>{a.child_name ? ` (${a.child_name})` : ""}</span>
      <span className="flex gap-2">
        {a.mode === "nominate" && a.status === "pending_review" && <button data-testid={`spons-approve-${a.alloc_id}`} onClick={() => onAction(reference, `allocations/${a.alloc_id}/approve`)} className="text-[#008057] font-semibold">Approve</button>}
        {ASSIGNABLE.includes(a.status) && <button onClick={assign} className="text-[#008057] font-semibold" data-testid={`spons-assign-${a.alloc_id}`}>Assign & Invite</button>}
        {a.beneficiary_registration_id && <span className="text-emerald-600 text-xs">Registered ✓</span>}
      </span>
    </div>
  );
}

function SponsorshipCard({ s, onAction }) {
  const paid = s.payment_status === "paid";
  return (
    <div className="rounded-2xl bg-white border border-[#E2EBF0] p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-mono font-bold text-[#03281B]">{s.reference}</span>
          <span className="ml-2 text-sm text-slate-500">{sponsorDisplayName(s)} &middot; {s.package?.name ? `${s.package.name} · ` : ""}{s.quantity} child(ren) &middot; {usd(s.total)}</span>
        </div>
        <SponsorStatusBadge s={s} />
      </div>
      <div className="text-xs text-slate-400 mt-1">Terms &amp; Privacy accepted: {fmtUtc(s.terms_accepted_at)}</div>
      <div className="flex flex-wrap gap-2 mt-3">
        {s.status === "pending_payment" && <button onClick={() => onAction(s.reference, "confirm-payment")} className="rounded-full bg-[#008057] text-white px-4 py-1.5 text-sm font-bold" data-testid={`spons-confirm-${s.reference}`}>Confirm Payment</button>}
        {s.status !== "cancelled" && <button data-testid={`spons-cancel-${s.reference}`} onClick={() => onAction(s.reference, "cancel")} className="rounded-full border-2 border-red-300 text-red-600 px-4 py-1.5 text-sm font-bold">Cancel</button>}
        {s.scarf_status !== "fulfilled" && paid && <button data-testid={`spons-scarf-${s.reference}`} onClick={() => onAction(s.reference, "scarf-fulfilled")} className="rounded-full border-2 border-[#E2EBF0] px-4 py-1.5 text-sm">Mark Scarf Sent</button>}
      </div>
      {paid && (
        <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
          {s.allocations.map((a) => <AllocationRow key={a.alloc_id} a={a} reference={s.reference} onAction={onAction} />)}
        </div>
      )}
    </div>
  );
}

export function SponsorshipsPanel({ spons, onAction, onExport }) {
  const st = spons.stats;
  return (
    <div>
      <H1>Sponsorship Program</H1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Heart} label="Total Sponsorships" value={st.total_sponsorships} tone="gold" />
        <Stat icon={CheckCircle2} label="Confirmed" value={st.confirmed} />
        <Stat icon={Users} label="Children Sponsored" value={st.children_sponsored} />
        <Stat icon={Clock} label="Awaiting Assignment" value={st.awaiting_assignment} tone="amber" />
        <Stat icon={DollarSign} label="Revenue" value={usd(st.revenue)} />
        <Stat icon={DollarSign} label="Outstanding" value={usd(st.outstanding)} tone="amber" />
        <button data-testid="export-sponsors" onClick={() => onExport("sponsors")} className="rounded-2xl bg-white border border-[#E2EBF0] p-5 text-left hover:border-[#008057]"><Download className="text-[#008057]" /><div className="font-bold text-[#03281B] mt-2 text-sm">Export Sponsors CSV</div></button>
      </div>
      <div className="mt-6 space-y-4">
        {spons.sponsorships.map((s) => <SponsorshipCard key={s.reference} s={s} onAction={onAction} />)}
        {spons.sponsorships.length === 0 && <div className="text-center text-slate-400 py-10">No sponsorships yet.</div>}
      </div>
    </div>
  );
}
